import React from 'react';
import PropTypes from 'prop-types';
import URL from 'urijs';
import _ from 'underscore';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SearchBox } from '@bingads-webui-react/search-box-control';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { CustomerList } from './customer-list';
import { getStyles } from './getStyles';

export class CustomerSelectorUnthemed extends React.PureComponent {
  constructor(props) {
    super(props);
    this.perfMarker = props.perfMarker;
    if (this.perfMarker) {
      this.perfMarkerSelect = this.perfMarker.createChild('MultiUser_Dropdown_Select');
      this.perfMarkerSelect.willInit();
    }
    this.viewAllCustomersUrl = '';
    this.customers = [];
    this.state = {
      loading: false,
      searchText: '',
    };
  }

  componentDidMount() {
    this.fetchCustomerList();
  }

  onSearchTextChanged = (value) => {
    this.setState({ searchText: value.toLowerCase() });
  };

  getDisplayAccountCount(numberOfAccounts) {
    const locString = numberOfAccounts === 1 ? this.props.i18n.getString(_TL_('{{numberOfAccounts}} account')) :
      this.props.i18n.getString(_TL_('{{numberOfAccounts}} accounts'));
    return curlyTemplate(locString)({ numberOfAccounts });
  }

  getDisplayName(customerName, isDefault) {
    return customerName + (isDefault ? this.props.i18n.getString(_TL_(' (default)')) : '');
  }

  fetchCustomerList() {
    if (this.perfMarkerSelect && _.isFunction(this.perfMarkerSelect.willFetchData)) {
      this.perfMarkerSelect.willFetchData();
    }

    this.setState({ loading: true });
    this.props.customerListService.getCustomers()
      .then(data => this.initializeCustomers(data))
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  initializeCustomers(data) {
    if (data && !data.Errors && data.customers) {
      const fullCustomersList = data.customers.map(customer => ({
        ...customer,
        DisplayAccountCount: this.getDisplayAccountCount(customer.NumberOfAccounts),
        DisplayName: this.getDisplayName(customer.Name, customer.IsDefault),
        IsCurrent: customer.Id === this.props.customerId,
      }));

      this.viewAllCustomersUrl = data.viewAllCustomersUrl.toLowerCase();
      this.customers = fullCustomersList;
    }

    this.setState({ loading: false });
    if (this.perfMarkerSelect && _.isFunction(this.perfMarkerSelect.willFetchData)) {
      this.perfMarkerSelect.willRender();
    }
    if (this.perfMarkerSelect &&
      _.isFunction(this.perfMarkerSelect.done) &&
      this.perfMarkerSelect.markers.timeRender) {
      this.perfMarkerSelect.done();
    }
  }

  render() {
    const searchTextPlaceHolder = this.props.isCustomerHierarchyEnabled ?
      _TL_('Enter manager account name, number or ID') : _TL_('Search accounts');
    return (
      <UiBlocker blocking={this.state.loading} loadingText={this.props.i18n.getString(_TL_('Loading ...'))}>
        <SearchBox
          onQueryChange={this.onSearchTextChanged}
          onSearch={() => {}}
          placeholder={this.props.i18n.getString(searchTextPlaceHolder)}
          value={this.state.searchText}
          searchBoxClasses={[this.props.classes.searchBox]}
        />
        <CustomerList
          searchText={this.state.searchText}
          customers={this.customers}
          i18n={this.props.i18n}
          url={this.props.url}
          customerUrlUpdater={this.props.customerUrlUpdater}
          viewAllCustomersUrl={this.viewAllCustomersUrl}
          maxNumberofRecentCustomerShown={this.props.maxNumberofRecentCustomerShown}
          classes={this.props.classes}
          isCustomerHierarchyEnabled={this.props.isCustomerHierarchyEnabled}
          renderLink={this.props.renderLink}
          isViewAllCustomerShowAsFallback={this.props.isViewAllCustomerShowAsFallback}
        />
      </UiBlocker>
    );
  }
}

CustomerSelectorUnthemed.propTypes = {
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  customerId: PropTypes.number.isRequired,
  customerListService: PropTypes.shape({ getCustomers: PropTypes.func }).isRequired,
  classes: PropTypes.shape({
    searchBox: PropTypes.string,
    customerList: PropTypes.string,
    customerListItem: PropTypes.string,
    manageCustomersHeader: PropTypes.string,
    recentCustomersHeader: PropTypes.string,
    customerListHeaderTitle: PropTypes.string,
    currentCustomerListItem: PropTypes.string,
    customerTileFirstLine: PropTypes.string,
    customerTitle: PropTypes.string,
    accountCount: PropTypes.string,
    accountId: PropTypes.string,
  }).isRequired,
  url: PropTypes.string.isRequired,
  maxNumberofRecentCustomerShown: PropTypes.number,
  customerUrlUpdater: PropTypes.func,
  isCustomerHierarchyEnabled: PropTypes.bool,
  renderLink: PropTypes.func,
  isViewAllCustomerShowAsFallback: PropTypes.bool,
  perfMarker: PropTypes.objectOf(PropTypes.func),
};

const DefaultRenderLink = ({ href, className, children }) =>
  <a href={href} className={className}>{children}</a>;
DefaultRenderLink.propTypes = {
  href: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

CustomerSelectorUnthemed.defaultProps = {
  isCustomerHierarchyEnabled: true,
  isViewAllCustomerShowAsFallback: false,
  maxNumberofRecentCustomerShown: 3,
  customerUrlUpdater: ({ url, cid, uid }) =>
    new URL(url).removeQuery('aid').setQuery('cid', cid).setQuery('uid', uid)
      .toString(),
  renderLink: DefaultRenderLink,
  perfMarker: null,
};

export const CustomerSelector = withDefaultStyles(CustomerSelectorUnthemed, getStyles);
